import React, { useState, useRef } from "react";
import { Dropdown, Icon } from "semantic-ui-react";
import { useHistory, useLocation } from "react-router-dom";
import { filter, groupBy, map, size, sortBy } from "lodash";
import { useOrder, useBeep, usePolling, BEEP_READY } from "../../../hooks";
import { ORDER_STATUS } from "../../../utils/constants";
import { getTableNumber, getTablePlace } from "../../../utils/table";
import { getIncompleteBatchTables } from "../../../utils/order";

const POLL_MS = 10000;

// Aviso al mozo en la barra superior: cuántos platos marcó cocina como
// "Listo" y faltan llevar, con un pitido cuando aparece uno nuevo — así no
// tiene que estar mirando la lista de mesas. Clic → lista por mesa, y cada
// mesa lleva a su detalle para marcarlos "Entregado".
export function ReadyAlert() {
  const { getOpenOrders } = useOrder();
  const { beep } = useBeep(BEEP_READY);
  const history = useHistory();
  const { pathname } = useLocation();
  const [readyOrders, setReadyOrders] = useState([]);
  const previousIdsRef = useRef(null);
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  const load = async () => {
    try {
      const response = await getOpenOrders();
      // WhatsApp / Venta rápida / Delivery salen en un solo lote: no se avisa
      // hasta que todo el pedido esté listo.
      const incomplete = getIncompleteBatchTables(response);
      const ready = filter(
        response,
        (order) =>
          order.status === ORDER_STATUS.READY &&
          order.table &&
          !incomplete.has(order.table),
      );
      const previous = previousIdsRef.current;
      // La tablet de cocina también tiene esta barra: ahí no suena (el
      // "Listo" lo acaba de tocar la propia cocina).
      if (
        previous &&
        pathnameRef.current !== "/admin/kitchen" &&
        ready.some((order) => !previous.has(order.id))
      ) {
        beep();
      }
      previousIdsRef.current = new Set(map(ready, "id"));
      setReadyOrders(ready);
    } catch (error) {
      // sin conexión: se reintenta en el próximo ciclo
    }
  };
  usePolling(load, POLL_MS, { immediate: true });

  if (size(readyOrders) === 0) return null;

  const byTable = sortBy(
    map(groupBy(readyOrders, "table"), (orders) => ({
      table: orders[0].table,
      number: getTableNumber(orders[0]),
      place: getTablePlace(orders[0]),
      lines: map(
        groupBy(
          orders,
          (order) => order.product_data?.title || "Producto eliminado",
        ),
        (sameOrders, title) => `${size(sameOrders)} × ${title}`,
      ),
    })),
    "number",
  );

  return (
    <Dropdown
      item
      icon={null}
      className="top-menu-admin__ready"
      trigger={
        <span title="Platos listos para llevar">
          <Icon name="bell" />
          {size(readyOrders)}
          <span className="top-menu-admin__ready-text">
            {" "}
            listo{size(readyOrders) > 1 ? "s" : ""}
          </span>
        </span>
      }
    >
      <Dropdown.Menu>
        <Dropdown.Header content="Listos para llevar" />
        {map(byTable, (row) => (
          <Dropdown.Item
            key={row.table}
            onClick={() => history.push(`/admin/table/${row.table}`)}
          >
            <strong>{row.place}</strong>
            <div className="top-menu-admin__ready-lines">
              {row.lines.join(" · ")}
            </div>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
}
